import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { COMMON_MESSAGES } from 'appRootPath/messages';
import { CpDocumentationActionsService } from 'appRootPath/services/cp-documentation-actions.service';

import { CpMarketplaceApiService } from 'appRootPath/services/cp-marketplace-api.service';
import { CpSigningUrlsService } from 'appRootPath/services/cp-signing-urls.service';
import {
  DEAL_CONFIRMATION_NOTE,
  DocumentObject,
  DOCUMENT_NAME_MAP,
  MODAL_OPTIONS,
  ONLY_ESIGN,
  ONLY_UPLOAD,
  UPLOAD_OR_ESIGN
} from 'appRootPath/shared/global-meta-data';
import { ViewDocsModalComponent } from 'appRootPath/shared/view-docs-modal/view-docs-modal.component';
import { uniqArrayByKey } from 'appRootPath/utils';

import { ToastrService } from 'ngx-toastr';
import {
  UploadSignedDocsConfirmComponent
} from '../upload-signed-docs-confirm/upload-signed-docs-confirm.component';

const dcnTypes = {
  UPLOAD: 'upload',
  SIGN: 'sign',
  BOTH: 'both'
};

@Component({
  selector: 'app-upload-sign-docs-form',
  templateUrl: './upload-sign-docs-form.component.html',
  styleUrls: ['./upload-sign-docs-form.component.scss']
})
export class UploadSignDocsFormComponent implements OnInit, OnChanges {
  @Input() modalInputs = {
    isInitialSign: true,
    issue: null,
    user: null,
    shouldMakeTransition: true,
    signatoriesList: [],
    documents: []
  };
  @Input() container = 'modal';
  @Input() commonSignatories = [];

  @Output() formSubmitOrCancelEvent = new EventEmitter();
  signDocumentsForm: FormGroup;
  uploadDcnId = 0;
  signDcnId = 0;

  // Below Constants are for deciding signing options.
  onlyUpload = ONLY_UPLOAD;
  onlyEsign = ONLY_ESIGN;
  uploadOrEsign = UPLOAD_OR_ESIGN;

  documentNamesMap = DOCUMENT_NAME_MAP;
  dcnKeyName = DEAL_CONFIRMATION_NOTE;
  fileTypes = ['.pdf'];

  containerTypes = {
    MODAL: 'modal',
    PANEL: 'sidepanel'
  };

  docIsDisplayedMap = {};
  allDocsShown = false;
  constructor(
    private apiService: CpMarketplaceApiService,
    private toastr: ToastrService,
    private formBuilder: FormBuilder,
    private signingService: CpSigningUrlsService,
    private modalService: NgbModal,
    private cpDocsService: CpDocumentationActionsService

  ) { }

  /**
   * On Init Lifecycle Hook
   * intialize the signatory formgroup with previous signatory values or blank
   */
  ngOnInit() {
    this.signDocumentsForm = this.formBuilder.group({
      sign_documents: this.makeSigningForm(this.modalInputs.documents)
    });
    this.addAuxillaryViewData(this.signDocumentsForm.get('sign_documents').value);
    this.uploadDcnId = 0;
    this.signDcnId = 0;
  }

  /**
   * Create object to track hiding/showing of individual document form fields
   * for accordion view in sidepanel
   */
  addAuxillaryViewData(documents) {
    this.docIsDisplayedMap = {};
    // if (document.signing_option === this.onlyEsign) {
    documents.forEach((doc) => {
      this.docIsDisplayedMap[doc.docKey] =
        this.container === this.containerTypes.MODAL ? true : false;
    });
  }

  /**
   * onchanges lifecycle hook
   * when commonSignatories input value changes, append the common signatories
   * to the signatory list for each of the docs to be sent for signing
   * @param changes changes object detecting changes in inputs of the component
   */
  ngOnChanges(changes) {
    if (changes && changes.commonSignatories &&
      changes.commonSignatories.currentValue &&
      changes.commonSignatories.currentValue.length > 0 &&
      this.signDocumentsForm) {
      const docSignatories = this.signDocumentsForm.get('sign_documents') as FormArray;

      for (const docToSign of docSignatories.controls) {
        if (docToSign.get('signing_option').value !== ONLY_UPLOAD) {
          const existingSignatories = docToSign.get('selectedSignatories').value;
          const newSignatories = uniqArrayByKey(
            existingSignatories.concat(changes.commonSignatories.currentValue),
            'id');
          docToSign.get('selectedSignatories').patchValue(newSignatories);
          docToSign.markAsDirty();
        }
      }
      this.signDocumentsForm.markAsDirty();
    }
  }

  /**
   * Creates the doc signatory form using the component input documents.
   * 1. match the doc signatory with the signatory list and assign to the form controls.
   * 2. generate the unique html ids for the form controls.
   * 3. patches the formgroup with previous signatories and other attributes
   * @param documents the existing documents list containing past signatories if any.
   */
  makeSigningForm(documents: DocumentObject[]) {
    return this.formBuilder.array(documents.
      filter(document => document.is_additional_data_added).
      map((doc) => {
        const signTypeAndId = this.getDocSignTypeAndId(doc);

        const foundSig =
          this.cpDocsService.parseFoundSignatories(doc.signatories_list, this.modalInputs.signatoriesList);

        const docFormGroup = this.formBuilder.group({
          document_name: doc.document_name,
          document_url: doc.document_url,
          signing_option: doc.signing_option,
          doc_upload_html_id: signTypeAndId.docUploadHtmlId,
          doc_signatory_html_id: signTypeAndId.docSignatoryHtmlId,
          investor_id: doc.investor_company && doc.investor_company.id,
          investor_comp_name: doc.investor_company && doc.investor_company.company_name,
          selectedSignatories: null,
          uploadedSignedDoc: null,
          docKey: signTypeAndId.docSignatoryHtmlId || signTypeAndId.docUploadHtmlId
        });

        docFormGroup.patchValue({
          selectedSignatories: foundSig
        });

        return docFormGroup;
      }));
  }

  /**
   * generates the unique input html ids based on the signing type of the document
   */
  getDocSignTypeAndId(document) {
    let signType;
    let docUploadHtmlId;
    let docSignatoryHtmlId;
    if (document.signing_option === this.onlyEsign) {
      signType = dcnTypes.SIGN;
      docSignatoryHtmlId = document.document_name === this.dcnKeyName ?
        `select_signatory_${document.document_name}_${this.signDcnId += 1}` :
        `select_signatory_${document.document_name}`;
    } else if (document.signing_option === this.onlyUpload) {
      signType = dcnTypes.UPLOAD;
      docUploadHtmlId = document.document_name === this.dcnKeyName ?
        `upload_document_${document.document_name}_${this.uploadDcnId += 1}` :
        `upload_document_${document.document_name}`;
    } else {
      docSignatoryHtmlId = `select_signatory_${document.document_name}`;
      docUploadHtmlId = `upload_document_${document.document_name}`;
      signType = dcnTypes.BOTH;

    }

    return {
      docUploadHtmlId,
      docSignatoryHtmlId
    };
  }

  /**
 * Handler for the remove selected signatory action of the ngselect control
 * @param docIdx the document index in the perdocsignatory form array
 * @param signatoryId the signatory to remove form the doc signatory list
 */
  removeDocSignatory(docIdx, signatoryId) {
    const docSignatories =
      (<FormArray>this.signDocumentsForm.get('sign_documents')).at(docIdx).get('selectedSignatories');
    docSignatories.patchValue(docSignatories.value.filter(value => value.id !== signatoryId));
  }

  /**
   * File upload for promissory note. patch the form control with the file
   */
  onUploadFileChange(docFile, documentDetails) {
    documentDetails.patchValue({
      uploadedSignedDoc: docFile
    });

    documentDetails.markAsDirty();
  }

  /**
   * show the pdf document in the View Docs modal
   */
  showDocument(documentKey: string, documentUrl: string) {
    const modalRef = this.modalService.open(ViewDocsModalComponent, MODAL_OPTIONS);
    modalRef.componentInstance.docsToView = [{
      title: DOCUMENT_NAME_MAP[documentKey],
      document_url: documentUrl
    }];
  }

  /**
   * Created the request object to send signatories/ upload signed docs for the current state docs.
   * Makes API call to send doc signatory data.
   */
  postSignedDocuments() {
    const documentsToBeSend = [];

    for (const docFc of this.signDocumentsForm.get('sign_documents')['controls']) {
      if (docFc.dirty) {
        const docFcVal = docFc.value;
        if (docFc.value.selectedSignatories || docFc.value.uploadedSignedDoc) {
          documentsToBeSend.push(docFcVal);
        }
      }
    }

    this.getUserConfirmation(documentsToBeSend).result.then((isAcceptedByUser) => {
      if (isAcceptedByUser) {
        this.moveState(documentsToBeSend);
      } else {
        // Do Nothing for now
      }
    });
  }

  /**
   * Show confirmation modal with signatory/document data and return user confirmation modal data
   * @param documentsToBeSend tentative documents to be sent for signing/uploaded
   */
  getUserConfirmation(documentsToBeSend): NgbModalRef {
    const modalRef = this.modalService.open(UploadSignedDocsConfirmComponent, MODAL_OPTIONS);
    modalRef.componentInstance.uploadSignData = documentsToBeSend;
    return modalRef;
  }

  /**
   * makes API call to submit the signatory/upload data.
   * @param documentsToSend the signatory/upload data for the each of the current state docs.
   */
  moveState(documentsToSend) {

    const apiRequestData = Object.assign(
      {
        action: 'accept',
        issue: this.modalInputs.issue,
        user: this.modalInputs.user
      },
      this.getDocumentsRequestData(documentsToSend)
    );

    // !ToDo Add move state API response interface
    // tslint:disable-next-line: deprecation
    this.apiService.moveState(this.modalInputs.issue, apiRequestData).subscribe((res: any) => {
      if (res.status === 'success') {
        if (res.apiResponse && res.apiResponse.signing_details) {
          this.signingService.setUrls(res.apiResponse.signing_details);
        }
        this.toastr.success(this.getToastrMessage(apiRequestData));
        this.formSubmitOrCancelEvent.emit(true);
      }
    });
  }

  /**
   * Gets the toaster message to show depending on the
   * API response for move state/ submit signatory/upload data
   * @param apiRequestData the request data sent to moveState
   */
  getToastrMessage(apiRequestData) {
    const { documents } = this.modalInputs;
    const docsForOnlyEsign = documents.filter(doc => doc.signing_option === this.onlyEsign);
    const docsForOnlyUpload = documents.filter(doc => doc.signing_option === this.onlyUpload);
    const docsForBoth = documents.filter(doc => doc.signing_option === this.uploadOrEsign);
    const allUploaded = !!apiRequestData.uploaded_documents.length && !apiRequestData.signatories.length;
    const allSigned = !!apiRequestData.signatories.length && !apiRequestData.uploaded_documents.length;

    if (docsForBoth.length) {
      if (allUploaded) {
        return COMMON_MESSAGES.DOCS_UPLOADED_SIGNED_ONLY;
      } else if (allSigned) {
        return COMMON_MESSAGES.DOCS_SENT_FOR_SIGNING_ONLY;
      } else {
        return COMMON_MESSAGES.DOCS_SIGNING_UPLOAD_OR_ESIGN;
      }
    } else if (docsForOnlyEsign.length && docsForOnlyUpload.length) {
      return COMMON_MESSAGES.DOCS_SIGNING_UPLOAD_OR_ESIGN;
    } else if (docsForOnlyEsign.length && !docsForOnlyUpload.length) {
      return COMMON_MESSAGES.DOCS_SENT_FOR_SIGNING_ONLY;
    } else if (!docsForOnlyEsign.length && docsForOnlyUpload.length) {
      return COMMON_MESSAGES.DOCS_UPLOADED_SIGNED_ONLY;
    }
  }

  /**
   * returns the signatory/uploaded doc arrays for each doc to be used for moveState API call.
   * @param documents the document data along with signatory/uploaded docs.
   */
  getDocumentsRequestData(documents) {
    const signatories = [];
    const uploaded_documents = [];

    documents.forEach(doc => {
      if (doc.selectedSignatories && doc.selectedSignatories.length && !doc.uploadedSignedDoc) {
        signatories.push(this.getSelectSignatoriesPostDetails(doc));
      } else if (!(doc.selectedSignatories && doc.selectedSignatories.length) && doc.uploadedSignedDoc) {
        uploaded_documents.push(this.getUploadrequestPostDetails(doc));
      }
    });

    return { signatories, uploaded_documents };
  }

  /**
   * returns the signatory object for each document in API acceptable format.
   * @param document document form data
   */
  getSelectSignatoriesPostDetails(document) {
    const postDetails = {
      docname: document.document_name,
      signatories: document.selectedSignatories.map(sig => sig.id),
      investor_id: document.investor_id
    };

    return postDetails;
  }

  /**
   * returns the upload signed doc object for each document in API acceptable format.
   * @param document document form data
   */
  getUploadrequestPostDetails(document) {
    let postDetails;

    if (document.document_name === DEAL_CONFIRMATION_NOTE) {
      postDetails = {
        document_name: document.document_name,
        document: null,
        document_list: [
          {
            company_id: document.investor_id,
            document: document.uploadedSignedDoc
          }]
      };
    } else {
      postDetails = {
        document_name: document.document_name,
        document: document.uploadedSignedDoc,
        document_list: null
      };
    }

    return postDetails;
  }

  /**
   * cancel the current changes to the form and close the sidepanel/modal
   */
  cancelFormChanges() {
    this.formSubmitOrCancelEvent.emit(false);
  }

  /**
   * Expand Collapse all doc accordions
   */
  toggleAllDocs() {
    const allDocs = this.signDocumentsForm.get('sign_documents').value;
    this.allDocsShown = !this.allDocsShown;

    allDocs.forEach((doc) => {
      this.toggleDocumentSection(doc, this.allDocsShown);
    });
  }
  /**
   * Toggle document section display
   */
  toggleDocumentSection(doc, viewStatus) {
    if (typeof viewStatus !== 'undefined') {
      this.docIsDisplayedMap[doc.docKey] = viewStatus;
    } else {
      this.docIsDisplayedMap[doc.docKey] = !this.docIsDisplayedMap[doc.docKey];
    }
  }

}
